import { getSupabase } from "./supabase";

/**
 * Filing ingestion pipeline:
 *   1. uploadFilingPdf  — push the raw PDF to the filings-raw bucket
 *   2. parseFilingPdf   — call the parse-filing Edge Function (Claude)
 *   3. ingestParsedFiling — atomic insert into Lawdog corpus + workspace
 *
 * The pipeline is split into three steps so the user can review the
 * parsed structure (between steps 2 and 3) before committing it to
 * the corpus. Step 3 returns a case_id the SPA uses to navigate to
 * the war room.
 */

// ---------- types ----------

export interface ParsedCase {
  case_number: string;
  court: string;
  jurisdiction: string;
  caption: string;
  filed_at: string | null;
}

export interface ParsedFilingMeta {
  filing_kind: string;
  title: string;
  filed_by: string | null;
  is_operative: boolean;
}

export interface ParsedSection {
  index: number;
  section_kind: string;
  ordinal: number | null;
  title: string | null;
  legal_basis: string | null;
}

export interface ParsedParagraph {
  section_index: number;
  paragraph_number: number;
  body: string;
  target: string | null;
}

export interface ParsedParty {
  display_name: string;
  party_type: "individual" | "entity";
  role: string;
  capacities: string[];
}

export interface ParsedFiling {
  case: ParsedCase;
  filing: ParsedFilingMeta;
  parties: ParsedParty[];
  sections: ParsedSection[];
  paragraphs: ParsedParagraph[];
}

/**
 * What the async-version of parse-filing returns:
 * the meta is committed server-side already (case + parties + sections),
 * paragraphs are extracting in the background.
 */
export interface ParseFilingAsyncResult {
  case_id: string;
  filing_id: string;
  job_id: string;
  status: "extracting_paragraphs";
  case: ParsedCase;
  filing: ParsedFilingMeta;
  parties: ParsedParty[];
  sections: ParsedSection[];
}

// ---------- step 1: upload ----------

export async function uploadFilingPdf(file: File): Promise<string> {
  const supabase = getSupabase();
  if (!supabase) throw new Error("Supabase not configured");

  const {
    data: { user },
    error: userErr,
  } = await supabase.auth.getUser();
  if (userErr || !user) {
    throw new Error("Not authenticated — sign in first");
  }

  const filingUuid = crypto.randomUUID();
  const path = `${user.id}/${filingUuid}/source.pdf`;

  const { error } = await supabase.storage
    .from("filings-raw")
    .upload(path, file, {
      contentType: "application/pdf",
      upsert: false,
    });

  if (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }

  return path;
}

// ---------- step 2: parse ----------

export async function parseFilingPdf(
  storagePath: string,
): Promise<ParseFilingAsyncResult> {
  const supabase = getSupabase();
  if (!supabase) throw new Error("Supabase not configured");

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("Not signed in");

  const baseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const res = await fetch(`${baseUrl}/functions/v1/parse-filing`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      apikey: anonKey,
    },
    body: JSON.stringify({ storage_path: storagePath }),
  });

  const text = await res.text();

  if (!res.ok) {
    let detail = text;
    try {
      const parsed = JSON.parse(text);
      detail = parsed.error ?? text;
    } catch {}
    throw new Error(`Parse failed (HTTP ${res.status}): ${detail}`);
  }

  let data: ParseFilingAsyncResult;
  try {
    data = JSON.parse(text) as ParseFilingAsyncResult;
  } catch {
    throw new Error(`Parser returned non-JSON response: ${text.slice(0, 200)}`);
  }

  if (!data || !data.case_id || !data.job_id) {
    throw new Error("Parser returned invalid response (missing case_id or job_id)");
  }

  return data;
}

// ---------- step 3: ingest ----------

export async function ingestParsedFiling(
  parsed: ParsedFiling,
  storagePath: string,
): Promise<string> {
  const supabase = getSupabase();
  if (!supabase) throw new Error("Supabase not configured");

  const { data, error } = await supabase
    .schema("berserkr")
    .rpc("ingest_parsed_filing", {
      p_parsed: parsed,
      p_storage_path: storagePath,
    });

  if (error) {
    throw new Error(`Ingest failed: ${error.message}`);
  }
  if (!data) {
    throw new Error("Ingest returned no case_id");
  }

  return data as string;
}

// ---------- anchor: date of service ----------

/** Filing kinds that open a responsive-pleading clock and require a service date. */
export function isResponseTriggering(filingKind: string | null | undefined): boolean {
  return /(complaint|counterclaim|crossclaim)/i.test(filingKind ?? "");
}

/**
 * Anchor the responsive-pleading clock from a date of service. The server
 * computes the deadline from the codified rules (NCRCP 15(a)/12(a)(1)).
 */
export async function setServiceAnchor(
  caseId: string,
  filingId: string,
  servedAt: string,
): Promise<string> {
  const supabase = getSupabase();
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase
    .schema("berserkr")
    .rpc("set_case_service_anchor", {
      p_case_id: caseId,
      p_filing_id: filingId,
      p_served_at: servedAt,
    });
  if (error) throw new Error(`Could not set date of service: ${error.message}`);
  return data as string;
}
